import classNames from 'classnames';
import {useCartProduct} from 'modules/cart/hooks/useCartProduct';
import {SubscriptionButton} from 'modules/product-subscription/components/SubscriptionButton';
import {IProductShort, TProductCardVariant, TProductListView} from 'modules/products/types';
import React, {FC, ReactNode, useMemo} from 'react';
import style from './style.module.scss';
import {InStoreButton} from 'modules/product-card-actions/components/InStoreButton';
import {AddToCartBlock} from 'modules/cart/components/AddToCartBlock';
import {TButtonSize} from 'components/Button';
import {IAnalyticsMeta} from 'core/types';
import {useIsPrive} from 'core/hooks/useIsPrive';

interface IProps extends IProductShort {
    className?: string;
    variant: TProductListView | TProductCardVariant;
    stretched?: boolean;
    showPrice?: boolean;
    isHorizontal?: boolean;
    isStaticWidth?: boolean;
    showAvailability?: boolean;
    btnSize?: TButtonSize;
    analytics?: IAnalyticsMeta;
}

export const ProductCardActions: FC<IProps> = ({
    showPrice = false,
    className,
    variant,
    stretched = false,
    isHorizontal = false,
    isStaticWidth = false,
    showAvailability = true,
    btnSize,
    analytics,
    ...product
}) => {
    const {canBuy, canReserve, id, offerId} = product;

    const {cartProductCount} = useCartProduct({
        id: Number(id),
        offerId,
    });

    const isPrive = useIsPrive();

    const renderCartOrSubscribe = useMemo<ReactNode>(() => {
        if (canBuy) {
            return (
                <AddToCartBlock
                    {...product}
                    btnSize={btnSize}
                    isHorizontal={isHorizontal}
                    isStaticWidth={isStaticWidth}
                    showPrice={showPrice}
                    stretched={stretched}
                    variant={variant}
                    analytics={analytics}
                />
            );
        }

        if (canReserve && !isPrive) {
            return <InStoreButton {...(product as unknown as IProductShort)} />;
        }

        return (
            <div className={classNames(style.subscriptionWrapper)}>
                <SubscriptionButton
                    className={classNames(style.productCardAdmissionButton)}
                    href={product.url}
                    {...product}
                />
            </div>
        );
    }, [
        canBuy,
        canReserve,
        isPrive,
        variant,
        product,
        btnSize,
        isHorizontal,
        isStaticWidth,
        showPrice,
        stretched,
        analytics,
    ]);

    const isProductInCart = useMemo(() => Boolean(cartProductCount), [cartProductCount]);

    return (
        <div
            className={classNames(className, style.productCardActions, 'product-card-actions', {
                [style[variant]]: variant,
                [style.productInCart]: isProductInCart,
                ['product-in-card']: isProductInCart,
            })}
            data-skeleton-item={true}
        >
            {renderCartOrSubscribe}
        </div>
    );
};
